import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { dressService, DressStatistics } from '../services/dress';
import { CalendarIcon, ArrowLeftIcon, ArrowDownTrayIcon, HeartIcon } from '@heroicons/react/24/outline';

const LikesReport: React.FC = () => {
  const [dressStatistics, setDressStatistics] = useState<DressStatistics[]>([]);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState<'last30' | 'last90' | 'lastMonth' | 'thisMonth' | 'custom' | 'all'>('last30');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [sortBy, setSortBy] = useState<'total' | 'catalog' | 'styleMatcher'>('total');
  const [showOnlyLiked, setShowOnlyLiked] = useState(false);

  useEffect(() => {
    loadDressStatistics();
  }, [dateFilter, customStartDate, customEndDate]);

  const getDateRange = () => {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    
    switch (dateFilter) {
      case 'last30':
        const thirtyDaysAgo = new Date(now);
        thirtyDaysAgo.setDate(now.getDate() - 30);
        return {
          startDate: thirtyDaysAgo.toISOString().split('T')[0],
          endDate: now.toISOString().split('T')[0]
        };
      case 'last90':
        const ninetyDaysAgo = new Date(now);
        ninetyDaysAgo.setDate(now.getDate() - 90);
        return {
          startDate: ninetyDaysAgo.toISOString().split('T')[0],
          endDate: now.toISOString().split('T')[0]
        };
      case 'lastMonth':
        const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const endOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
        return {
          startDate: lastMonth.toISOString().split('T')[0],
          endDate: endOfLastMonth.toISOString().split('T')[0]
        };
      case 'thisMonth':
        return {
          startDate: startOfMonth.toISOString().split('T')[0],
          endDate: endOfMonth.toISOString().split('T')[0]
        };
      case 'custom':
        return {
          startDate: customStartDate || undefined,
          endDate: customEndDate || undefined
        };
      default:
        return {};
    }
  };

  const loadDressStatistics = async () => {
    try {
      setLoading(true);
      const dateRange = getDateRange();
      const params: any = {};
      if (dateRange.startDate) params.startDate = dateRange.startDate;
      if (dateRange.endDate) params.endDate = dateRange.endDate;
      
      const response = await dressService.getDressStatistics(params);
      setDressStatistics(response.data);
    } catch (err) {
      console.error('Failed to load dress statistics:', err);
    } finally {
      setLoading(false);
    }
  };

  const getSortedStatistics = () => {
    let filtered = showOnlyLiked 
      ? dressStatistics.filter(stat => stat.totalLikes > 0)
      : dressStatistics;

    return [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'catalog':
          return b.catalogLikes - a.catalogLikes;
        case 'styleMatcher':
          return b.styleMatcherLikes - a.styleMatcherLikes;
        default:
          return b.totalLikes - a.totalLikes;
      }
    });
  };

  const exportToCSV = () => {
    const data = getSortedStatistics();
    const headers = ['Dress Name', 'Designer', 'Total Likes', 'Catalog Likes', 'Style Matcher Likes'];
    
    const csvContent = [
      headers.join(','),
      ...data.map(stat => [
        `"${stat.name}"`,
        `"${stat.designer || 'Unknown'}"`,
        stat.totalLikes,
        stat.catalogLikes,
        stat.styleMatcherLikes
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `dress-likes-report-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const formatDateRange = () => {
    const range = getDateRange();
    if (!range.startDate && !range.endDate) return 'All Time';
    if (range.startDate && range.endDate) {
      const start = new Date(range.startDate).toLocaleDateString();
      const end = new Date(range.endDate).toLocaleDateString();
      return `${start} - ${end}`;
    }
    return 'Custom Range';
  };

  const getTotalCounts = () => {
    return dressStatistics.reduce((acc, stat) => ({
      total: acc.total + stat.totalLikes,
      catalog: acc.catalog + stat.catalogLikes,
      styleMatcher: acc.styleMatcher + stat.styleMatcherLikes
    }), { total: 0, catalog: 0, styleMatcher: 0 });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg text-gray-600">Loading report...</div>
      </div>
    );
  }

  const sortedStatistics = getSortedStatistics();
  const totals = getTotalCounts();

  return (
    <div className="px-4 py-6 sm:px-0">
      <div className="sm:flex sm:items-center justify-between">
        <div>
          <Link
            to="/reports"
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-1" />
            Back to Reports
          </Link>
          <h1 className="text-xl font-semibold text-gray-900">Dress Likes Report</h1>
          <p className="mt-2 text-sm text-gray-700">
            Track dress popularity across catalog browsing and Style Matcher interactions.
          </p>
        </div>
        <button
          onClick={exportToCSV}
          className="mt-4 sm:mt-0 inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
        >
          <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
          Export CSV
        </button>
      </div>

      {/* Date Filter */}
      <div className="mt-6 bg-white rounded-lg shadow p-4">
        <div className="flex items-center mb-3">
          <CalendarIcon className="h-5 w-5 mr-2 text-gray-500" />
          <h3 className="text-sm font-medium text-gray-900">Date Range</h3>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setDateFilter('all')}
            className={`px-3 py-1 text-sm rounded-md ${
              dateFilter === 'all'
                ? 'bg-indigo-600 text-white'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            All Time
          </button>
          <button
            onClick={() => setDateFilter('last30')}
            className={`px-3 py-1 text-sm rounded-md ${
              dateFilter === 'last30'
                ? 'bg-indigo-600 text-white'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            Last 30 Days
          </button>
          <button
            onClick={() => setDateFilter('last90')}
            className={`px-3 py-1 text-sm rounded-md ${
              dateFilter === 'last90'
                ? 'bg-indigo-600 text-white'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            Last 90 Days
          </button>
          <button
            onClick={() => setDateFilter('lastMonth')}
            className={`px-3 py-1 text-sm rounded-md ${
              dateFilter === 'lastMonth'
                ? 'bg-indigo-600 text-white'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            Last Month
          </button>
          <button
            onClick={() => setDateFilter('thisMonth')}
            className={`px-3 py-1 text-sm rounded-md ${
              dateFilter === 'thisMonth'
                ? 'bg-indigo-600 text-white'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            This Month
          </button>
          <button
            onClick={() => {
              setDateFilter('custom');
              setShowDatePicker(true);
            }}
            className={`px-3 py-1 text-sm rounded-md ${
              dateFilter === 'custom'
                ? 'bg-indigo-600 text-white'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            Custom Range
          </button>
        </div>
        
        {dateFilter === 'custom' && showDatePicker && (
          <div className="mt-3 flex items-center gap-2">
            <input
              type="date"
              value={customStartDate}
              onChange={(e) => setCustomStartDate(e.target.value)}
              className="px-3 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            />
            <span className="text-sm text-gray-500">to</span>
            <input
              type="date"
              value={customEndDate}
              onChange={(e) => setCustomEndDate(e.target.value)}
              className="px-3 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
        )}
      </div>

      {/* Summary Stats */}
      <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-3">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <dt className="text-sm font-medium text-gray-500 truncate">Total Likes</dt>
            <dd className="mt-1 text-3xl font-semibold text-gray-900">{totals.total.toLocaleString()}</dd>
            <p className="mt-2 text-xs text-gray-500">{formatDateRange()}</p>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <dt className="text-sm font-medium text-gray-500 truncate">Catalog Likes</dt>
            <dd className="mt-1 text-3xl font-semibold text-gray-900">{totals.catalog.toLocaleString()}</dd>
            <p className="mt-2 text-xs text-gray-500">{((totals.catalog / totals.total) * 100).toFixed(1)}% of total</p>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <dt className="text-sm font-medium text-gray-500 truncate">Style Matcher Likes</dt>
            <dd className="mt-1 text-3xl font-semibold text-gray-900">{totals.styleMatcher.toLocaleString()}</dd>
            <p className="mt-2 text-xs text-gray-500">{((totals.styleMatcher / totals.total) * 100).toFixed(1)}% of total</p>
          </div>
        </div>
      </div>

      {/* Filters and Sorting */}
      <div className="mt-6 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={showOnlyLiked}
              onChange={(e) => setShowOnlyLiked(e.target.checked)}
              className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
            />
            <span className="ml-2 text-sm text-gray-700">Show only dresses with likes</span>
          </label>
        </div>
        <div className="flex items-center space-x-2">
          <label className="text-sm text-gray-700">Sort by:</label>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as any)}
            className="text-sm border border-gray-300 rounded-md px-3 py-1 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value="total">Total Likes</option>
            <option value="catalog">Catalog Likes</option>
            <option value="styleMatcher">Style Matcher Likes</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="mt-6 bg-white shadow overflow-hidden sm:rounded-md">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Dress
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Total Likes
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Catalog
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Style Matcher
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedStatistics.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-6 py-12 text-center text-gray-500">
                  No dress likes found for the selected period
                </td>
              </tr>
            ) : (
              sortedStatistics.map((stat) => (
                <tr key={stat.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{stat.name}</div>
                      <div className="text-sm text-gray-500">{stat.designer || 'Unknown Designer'}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end">
                      <HeartIcon className="h-4 w-4 text-red-500 mr-1" />
                      <span className="text-sm font-medium text-gray-900">{stat.totalLikes}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right text-sm text-gray-900">
                    {stat.catalogLikes}
                  </td>
                  <td className="px-6 py-4 text-right text-sm text-gray-900">
                    {stat.styleMatcherLikes}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default LikesReport;